import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../AuthContext";
import { Download, RefreshCw, Search, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const LEVEL_COLORS = {
  INFO: "text-sky-500 bg-sky-500/10",
  WARNING: "text-amber-500 bg-amber-500/10",
  ERROR: "text-rose-500 bg-rose-500/10",
  CRITICAL: "text-rose-600 bg-rose-600/15",
};

const PROCESSES = ["all", "api", "auth", "autopilot", "billing", "brief", "discovery", "mc",
  "newsletter", "podcast", "ranking", "scraper", "search", "system", "youtube", "other"];

const toLocalInput = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function LogExplorer({ dm }) {
  const { authHeader } = useAuth();
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    return { from: toLocalInput(new Date(now - 6 * 3600 * 1000)), to: toLocalInput(now), level: "INFO", process: "all", q: "" };
  });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [format, setFormat] = useState("csv");
  const [expanded, setExpanded] = useState(null);

  const params = useCallback((extra = {}) => ({
    from_ts: new Date(filters.from).toISOString(),
    to_ts: new Date(filters.to).toISOString(),
    level: filters.level,
    process: filters.process === "all" ? undefined : filters.process,
    q: filters.q || undefined,
    ...extra,
  }), [filters]);

  const fetchLogs = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const [logsRes, sumRes] = await Promise.all([
        axios.get(`${API}/api/admin/logs`, { headers: authHeader(), params: params({ page: pg, limit: 100 }) }),
        axios.get(`${API}/api/admin/logs/summary`, { headers: authHeader(), params: { from_ts: new Date(filters.from).toISOString(), to_ts: new Date(filters.to).toISOString() } }),
      ]);
      if (pg === 1) setItems(logsRes.data.items); else setItems(prev => [...prev, ...logsRes.data.items]);
      setTotal(logsRes.data.total);
      setPage(pg);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load logs");
    } finally { setLoading(false); }
  }, [authHeader, params, filters, items]);

  useEffect(() => { fetchLogs(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const preset = (hours) => {
    const now = new Date();
    setFilters(f => ({ ...f, from: toLocalInput(new Date(now - hours * 3600 * 1000)), to: toLocalInput(now) }));
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/admin/logs/export`, {
        headers: authHeader(), params: params({ format }), responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : `ti-logs.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Log export downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally { setDownloading(false); }
  };

  const inputCls = `text-xs rounded-lg px-2 py-1.5 border-0 outline-none ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"}`;

  return (
    <div data-testid="log-explorer" className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className={`editorial-heading text-xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Log Explorer</h3>
        {summary && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="log-summary">
            <span className={`category-tag px-2 py-0.5 rounded-full ${dm ? "bg-white/5 text-slate-400" : "bg-black/5 text-[#6b7a7d]"}`}>{summary.total} records</span>
            {["INFO", "WARNING", "ERROR"].map(lv => (summary.by_level?.[lv] || 0) > 0 && (
              <span key={lv} className={`category-tag px-2 py-0.5 rounded-full ${LEVEL_COLORS[lv]}`}>{lv} {summary.by_level[lv]}</span>
            ))}
            {(summary.pods || []).length > 1 && <span className={`category-tag px-2 py-0.5 rounded-full ${dm ? "bg-white/5 text-slate-400" : "bg-black/5 text-[#6b7a7d]"}`}>{summary.pods.length} pods</span>}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className={`block category-tag mb-1 ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>From</label>
          <input data-testid="log-filter-from" type="datetime-local" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className={`block category-tag mb-1 ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>To</label>
          <input data-testid="log-filter-to" type="datetime-local" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className={`block category-tag mb-1 ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>Process</label>
          <select data-testid="log-filter-process" value={filters.process} onChange={e => setFilters(f => ({ ...f, process: e.target.value }))} className={inputCls}>
            {PROCESSES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className={`block category-tag mb-1 ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>Level ≥</label>
          <select data-testid="log-filter-level" value={filters.level} onChange={e => setFilters(f => ({ ...f, level: e.target.value }))} className={inputCls}>
            <option value="INFO">INFO (all)</option>
            <option value="WARNING">WARNING</option>
            <option value="ERROR">ERROR</option>
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className={`block category-tag mb-1 ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>Search</label>
          <input data-testid="log-filter-search" value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} placeholder="Text in message…" className={`${inputCls} w-full`} onKeyPress={e => { if (e.key === "Enter") fetchLogs(1); }} />
        </div>
        <button data-testid="log-apply-btn" onClick={() => fetchLogs(1)} disabled={loading}
          className={`px-4 py-1.5 rounded-full category-tag flex items-center gap-1.5 disabled:opacity-50 ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>
          {loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}Apply
        </button>
        <div className="flex items-center gap-1">
          {[["1h", 1], ["6h", 6], ["24h", 24], ["7d", 168]].map(([lbl, h]) => (
            <button key={lbl} data-testid={`log-preset-${lbl}`} onClick={() => preset(h)}
              className={`px-2 py-1.5 rounded-lg category-tag ${dm ? "bg-white/5 text-slate-400 hover:text-slate-200" : "bg-black/5 text-[#6b7a7d] hover:text-[#191c1d]"}`}>{lbl}</button>
          ))}
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <select data-testid="log-format-select" value={format} onChange={e => setFormat(e.target.value)} className={inputCls}>
            <option value="csv">CSV</option>
            <option value="ndjson">NDJSON</option>
          </select>
          <button data-testid="log-download-btn" onClick={download} disabled={downloading}
            className={`px-4 py-1.5 rounded-full category-tag flex items-center gap-1.5 disabled:opacity-50 ${dm ? "bg-white/10 text-slate-200 hover:bg-white/15" : "bg-[#191c1d] text-white"}`}>
            {downloading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}Download
          </button>
        </div>
      </div>

      {/* Rows */}
      <div data-testid="log-rows" className={`rounded-xl overflow-hidden ${dm ? "bg-[#141a1b]" : "bg-[#fafbfb]"}`}>
        {items.length === 0 ? (
          <p className={`text-sm text-center py-8 ${dm ? "text-slate-600" : "text-[#8a999c]"}`}>{loading ? "Loading…" : "No log records in this range"}</p>
        ) : items.map((it, i) => (
          <div key={i} data-testid={`log-row-${i}`} onClick={() => setExpanded(expanded === i ? null : i)}
            className={`flex items-start gap-2 px-3 py-1.5 cursor-pointer border-b text-xs font-mono ${dm ? "border-white/4 hover:bg-white/3" : "border-black/4 hover:bg-black/2"}`}>
            <span className={`flex-shrink-0 w-32 ${dm ? "text-slate-600" : "text-[#9aa7a9]"}`}>{new Date(it.ts).toLocaleString("en-GB", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
            <span className={`flex-shrink-0 w-16 px-1 rounded text-center ${LEVEL_COLORS[it.level] || (dm ? "text-slate-400" : "text-[#6b7a7d]")}`}>{it.level}</span>
            <span className={`flex-shrink-0 w-20 ${dm ? "text-cyan-500/80" : "text-[#006874]"}`}>{it.process}</span>
            <span className={`${expanded === i ? "whitespace-pre-wrap break-all" : "truncate"} ${dm ? "text-slate-300" : "text-[#3c4a4d]"}`}>{it.msg}</span>
            <ChevronDown className={`w-3 h-3 flex-shrink-0 mt-0.5 transition-transform ${expanded === i ? "rotate-180" : ""} ${dm ? "text-slate-600" : "text-[#bac9cc]"}`} />
          </div>
        ))}
      </div>
      {items.length < total && (
        <div className="text-center mt-3">
          <button data-testid="log-load-more-btn" onClick={() => fetchLogs(page + 1)} disabled={loading}
            className={`px-4 py-1.5 rounded-full category-tag ${dm ? "bg-white/5 text-slate-300" : "bg-black/5 text-[#3c4a4d]"}`}>
            Load more ({items.length}/{total})
          </button>
        </div>
      )}
    </div>
  );
}
